import { mapActions, mapGetters } from 'vuex';
import Modals from '@store/modules/ui/shared/modal/modals';
import {
    min,
    max,
    isRequired,
    isDateInValidFormat,
} from '@modules/shared/validators/forms';
import { WithdrawalType } from '@models/user/enums';
import { routeNames } from '@app/router';
import {
    createBankTransferRequest,
    createInstantRequest,
    createOnlineRequest,
    createShopRequest,
    createPscTransferRequest,
} from '@models/user/withdrawal/WithdrawalRequest';
import ClientStorage from '@core/utils/storage/ClientStorage';
import WithdrawalForm from './forms/WithdrawalForm.vue';
import { errorGuard } from '@app/utils';
import UserService from '@core/services/user/UserService';
import config from '@config';

export default function (type: WithdrawalType) {
    return {
        name: 'WithdrawalTypeContainer',
        props: {
            type: {
                type: String,
                required: true,
            },
            shops: {
                type: Array,
                required: false,
                default() {
                    return [];
                }
            },
            needsUserData: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        data() {
            return {
                asyncErrors: {},
                fields: {},
            };
        },
        created() {
            const self = this as any;
            self.fields = self.getFormFields();
            self.routeNames = routeNames;
        },
        computed: {
            ...mapGetters('data/authentication', [
                'userBalance',
            ]),
            ...mapGetters('data/country', [
                'config',
            ]),
            ...mapGetters('data/authentication', [
                'user',
                'userBalanceWithBonus',
            ]),
        },
        methods: {
            ...mapActions('ui/modal', [
                'setModal',
            ]),
            getFormFields() {
                switch (type) {
                    case WithdrawalType.instant:
                        return this.agencyWithdrawalFormFields();
                    case WithdrawalType.shop:
                        return this.agencyWithdrawalFormFields();
                    case WithdrawalType.bankTransfer:
                        return this.onlineWithdrawalFormFields();
                    case WithdrawalType.online:
                        return this.onlineWithdrawalFormFields();
                    case WithdrawalType.paySafeCard:
                        return this.pscWithdrawalFormFields();
                    default:
                        return {};
                }
            },
            agencyWithdrawalFormFields() {
                return {
                    amount: this.amountFormField(),
                    shop: this.shopFormField(),
                };
            },
            onlineWithdrawalFormFields() {
                return {
                    amount: this.amountFormField(),
                };
            },
            pscWithdrawalFormFields() {
                const self = this as any;
                return {
                    amount: this.amountFormField(),
                    dateOfBirth: {
                        value: null,
                        validate: [
                            isRequired,
                            isDateInValidFormat(/^(((0)[0-9])|((1)[0-2]))\.([0-2][0-9]|(3)[0-1])\.\d{4}$/)
                        ]
                    },
                    firstName: {
                        value: '',
                        validate: [
                            isRequired,
                        ]
                    },
                    lastName: {
                        value: '',
                        validate: [
                            isRequired,
                        ]
                    },
                    email: {
                        value: '',
                        validate: [
                            isRequired,
                        ]
                    }
                };
            },
            amountFormField() {
                const self = this as any;
                return {
                    value: self.getDefaultAmount(),
                    validate: [
                        isRequired,
                        min(self.getMinAmount()),
                        max(self.getMaxAmount()),
                    ]
                };
            },
            shopFormField() {
                const self = this as any;
                return {
                    value: self.getLastSelectedShop(),
                    validate: [
                        isRequired,
                    ],
                };
            },
            async onSubmit() {
                const self = this as any;
                const payload = {
                    amount: self.fields.amount.value,
                    email: self.fields.email && self.fields.email.value,
                    firstName: self.fields.firstName && self.fields.firstName.value,
                    lastName: self.fields.lastName && self.fields.lastName.value,
                    dateOfBirth: self.fields.dateOfBirth && self.fields.dateOfBirth.value,
                    shopId: self.fields.shop && self.fields.shop.value
                };
                const withdrawalRequest = (({
                    [WithdrawalType.instant]: createInstantRequest,
                    [WithdrawalType.shop]: createShopRequest,
                    [WithdrawalType.bankTransfer]: createBankTransferRequest,
                    [WithdrawalType.online]: createOnlineRequest,
                    [WithdrawalType.paySafeCard]: createPscTransferRequest,
                }[type]) as Function)(payload);
                errorGuard({
                    action: async () => {
                        self.saveSelectedShop();
                        const withdrawalResponse = await UserService.getInstance().createWithdrawal(withdrawalRequest);

                        if ([WithdrawalType.online, WithdrawalType.bankTransfer, WithdrawalType.paySafeCard]
                            .includes(type)
                        ) {
                            self.setModal({
                                code: Modals.withdrawalOnlineResponse.code,
                                data: withdrawalResponse,
                            });
                        } else {
                            self.setModal({
                                code: Modals.withdrawalShopResponse.code,
                                data: withdrawalResponse,
                            });
                        }
                    },
                    onError: () => {
                        self.setModal(Modals.withdrawalError.code);
                    },
                });
            },
            getDefaultAmount() {
                const self = this as any;
                const instantAmount = self.userBalanceWithBonus
                    ? self.userBalanceWithBonus
                    : self.config.withdrawal.instantMin;
                const defaults = {
                    [WithdrawalType.instant]: Math.min(
                        Math.floor(instantAmount / 2),
                        parseInt(self.config.withdrawal.instantMax, 10)
                    ),
                    [WithdrawalType.shop]: self.config.withdrawal.shopMin,
                    [WithdrawalType.bankTransfer]: self.config.withdrawal.bankTransferMin,
                    [WithdrawalType.online]: self.config.withdrawal.onlineMin,
                    [WithdrawalType.paySafeCard]: self.config.withdrawal.bankTransferMin,
                };
                return parseInt(defaults[type], 10);
            },
            getMinAmount() {
                const self = this as any;
                const mins = {
                    [WithdrawalType.instant]: self.config.withdrawal.instantMin,
                    [WithdrawalType.shop]: self.config.withdrawal.shopMin,
                    [WithdrawalType.bankTransfer]: self.config.withdrawal.bankTransferMin,
                    [WithdrawalType.online]: self.config.withdrawal.onlineMin,
                    [WithdrawalType.paySafeCard]: self.config.withdrawal.bankTransferMin,
                };
                return parseInt((mins[type]), 10);
            },
            getMaxAmount() {
                const self = this as any;
                const maxs = {
                    [WithdrawalType.instant]: Math.min(self.config.withdrawal.instantMax, self.userBalance),
                    [WithdrawalType.shop]: Math.min(self.config.withdrawal.shopMax, self.userBalance),
                    [WithdrawalType.bankTransfer]: Math.min(self.config.withdrawal.bankTransferMax, self.userBalance),
                    [WithdrawalType.online]: Math.min(self.config.withdrawal.onlineMax, self.userBalance),
                    [WithdrawalType.paySafeCard]: Math.min(self.config.withdrawal.bankTransferMax, self.userBalance),
                };
                return parseInt(maxs[type].toString(), 10);
            },
            saveSelectedShop() {
                const self = this as any;
                if (self.fields.shop) {
                    const shops = ClientStorage.get('users:withdrawal:shops') || [];
                    shops.unshift(self.fields.shop.value);
                    ClientStorage.set('users:withdrawal:shops', shops);
                }
            },
            getLastSelectedShop() {
                const shops = ClientStorage.get('users:withdrawal:shops');
                return shops ? shops[0] : undefined;
            }
        },
        render(createElement: Function) {
            const self = this as any;
            return createElement(
                WithdrawalForm,
                {
                    props: {
                        fields: self.fields,
                        asyncErrors: self.asyncErrors,
                        shops: self.shops,
                        needsUserData: self.needsUserData,
                        withdrawalTaxLimits: self.config.withdrawal.tax,
                        isTaxable: config.countrySpecific.user.withdrawal.isTaxable,
                    },
                    on: {
                        submit: self.onSubmit,
                    }
                }
            );
        },
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/modules/userProfile/withdrawal/components/WithdrawalTypeContainer.ts